import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { pb, checkPocketBaseConnection } from '../lib/pocketbase';

interface User {
  id: string;
  email: string;
  username: string;
  firstname?: string;
  lastname?: string;
  phone?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  balance: number;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  addCredit: (amount: number) => Promise<void>;
  deductCredit: (amount: number) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [balance, setBalance] = useState(0);

  const fetchBalance = useCallback(async (userId: string) => {
    const maxRetries = 3;
    let retries = 0;

    while (retries < maxRetries) {
      try {
        const record = await pb.collection('v2t_balances').getFirstListItem(`user="${userId}"`, {
          $cancelKey: userId,
        });
        setBalance(record.amount);
        return;
      } catch (error: unknown) {
        console.error('Error fetching balance:', error);
        if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
          try {
            const newBalance = await pb.collection('v2t_balances').create({
              user: userId,
              amount: 0
            });
            setBalance(newBalance.amount);
            return;
          } catch (createError) {
            console.error('Error creating balance record:', createError);
          }
        }
        retries++;
        if (retries === maxRetries) {
          console.error('Max retries reached. Unable to fetch balance.');
          setBalance(0);
        } else {
          await new Promise(resolve => setTimeout(resolve, 1000 * retries));
        }
      }
    }
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      if (pb.authStore.isValid && pb.authStore.model) {
        try {
          // Get the current user record with full details
          const currentUser = await pb.collection('v2t_users').getOne(pb.authStore.model.id);
          
          if (currentUser) {
            setUser({
              id: currentUser.id,
              email: currentUser.email,
              username: currentUser.username,
              firstname: currentUser.firstname || '',
              lastname: currentUser.lastname || '',
              phone: currentUser.phone || '',
            });
            setIsAuthenticated(true);
            await fetchBalance(currentUser.id);
          } else {
            setIsAuthenticated(false);
            setUser(null);
            setBalance(0);
          }
        } catch (error: unknown) {
          console.error('Error loading user:', error);
          setIsAuthenticated(false);
          setUser(null);
          setBalance(0);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setBalance(0);
      }
    };

    loadUser();

    return () => {
      pb.cancelAllRequests();
    };
  }, [fetchBalance]);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      // Check PocketBase connection first
      const connectionStatus = await checkPocketBaseConnection();
      if (!connectionStatus.success) {
        console.error('PocketBase connection failed:', connectionStatus.message);
        throw new Error(`Cannot connect to PocketBase: ${connectionStatus.message}`);
      }
      
      // Attempt authentication
      const authData = await pb.collection('v2t_users').authWithPassword(email, password);
      
      if (authData.record) {
        // Get full user details after authentication
        const userRecord = await pb.collection('v2t_users').getOne(authData.record.id);
        
        setUser({
          id: userRecord.id,
          email: userRecord.email,
          username: userRecord.username,
          firstname: userRecord.firstname || '',
          lastname: userRecord.lastname || '',
          phone: userRecord.phone || '',
        });
        setIsAuthenticated(true);
        await fetchBalance(userRecord.id);
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Log detailed information about the error
      if (error.status === 405) {
        console.error('Method Not Allowed (405) error. This could be due to CORS issues or incorrect API endpoint.');
        console.error('PocketBase URL being used:', pb.baseUrl);
      } else if (error.status === 400) {
        console.error('Bad Request (400) error. This could be due to invalid credentials.');
      } else if (error.status === 404) {
        console.error('Not Found (404) error. The API endpoint might be incorrect.');
      }
      
      return false;
    }
  };

  const logout = async () => {
    pb.authStore.clear();
    setIsAuthenticated(false);
    setUser(null);
    setBalance(0);
  };

  const addCredit = async (amount: number) => {
    if (!user) throw new Error('User not authenticated');
    try {
      const record = await pb.collection('v2t_balances').getFirstListItem(`user="${user.id}"`);
      const updatedRecord = await pb.collection('v2t_balances').update(record.id, {
        amount: record.amount + amount,
      });
      setBalance(updatedRecord.amount);
    } catch (error) {
      console.error('Error adding credit:', error);
      throw error;
    }
  };

  const deductCredit = async (amount: number): Promise<boolean> => {
    if (!user) return false;
    try {
      const record = await pb.collection('v2t_balances').getFirstListItem(`user="${user.id}"`);
      if (record.amount >= amount) {
        const updatedRecord = await pb.collection('v2t_balances').update(record.id, {
          amount: record.amount - amount,
        });
        setBalance(updatedRecord.amount);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deducting credit:', error);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, balance, login, logout, addCredit, deductCredit }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};